import { forwardRef } from "react";
import { cx } from "cva";
import type { PropsWithChildren, ElementType, ComponentProps, ForwardedRef, JSX } from "react";
import type { TypographyVariants } from "@knitto-ui/tokens";

const typographyVariants = {
  button: "kn-typography-button",
  description: "kn-typography-description",
  h1: "kn-typography-h1",
  h2: "kn-typography-h2",
  h3: "kn-typography-h3",
  h4: "kn-typography-h4",
  h5: "kn-typography-h5",
  h6: "kn-typography-h6",
  hint: "kn-typography-hint",
  paragraph: "kn-typography-paragraph",
  paragraphBold: "kn-typography-paragraph-bold",
  reportLabel: "kn-typography-report-label",
  reportLabelBold: "kn-typography-report-label-bold",
  subtitle1: "kn-typography-subtitle-1",
  subtitle2: "kn-typography-subtitle-2",
  subtitle3: "kn-typography-subtitle-3",
} satisfies Record<TypographyVariants, string>;

// Defines the base props for the Typography component
type TypographyBaseProps<T extends ElementType> = PropsWithChildren<{
  as?: T;
  variant?: TypographyVariants;
}>;

// Extends the base props with additional component props
export type TypographyProps<T extends ElementType> = TypographyBaseProps<T> &
  Omit<ComponentProps<T>, keyof TypographyBaseProps<T>>;

// Defines the Typography component with generic type support
const Typography = <T extends ElementType = "div">(
  { className, as: asProp, variant = "paragraph", ...props }: TypographyProps<T>,
  ref: ForwardedRef<HTMLElement>,
) => {
  // Sets the component's element type based on the provided 'as' prop or defaults to 'div'
  const Component = asProp || ("div" as ElementType);

  // Renders the component with the appropriate styles and props
  return <Component {...props} ref={ref} className={cx(typographyVariants[variant], className)} />;
};

// Wraps the Typography component with a forwardRef
const ForwardedTypography = forwardRef(Typography);

// Sets the display name for the forwardRef-wrapped Typography component
ForwardedTypography.displayName = "Typography";

// Defines the exported Typography component type
type ExportedTypographyType = <T extends ElementType = "div">(
  props: TypographyProps<T>,
) => JSX.Element;

// Exports the Typography component as the default export
export default ForwardedTypography as ExportedTypographyType;
